import { createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
import {
  CaptureResultTuningValuesDTO,
  ColorPropertyDTO,
  DeleteScribbleDTO,
  ExperimentTuningValuesDTO,
  IWellImageDetailsDTO,
  IWellImageDTO,
  IWellImageParams,
  RegionDrawingDTO,
  RegionDropLocationDTO,
  ResponseType,
  ScribbleDrawingDTO as UpdateDrawingDTO,
  WellDropImportFlDTO,
  WellImageDTO,
  WellImageParams,
} from 'business/models/Models';
import CrystalService from 'business/services/CrystalService';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import WellService from 'business/services/WellService';
import { getDropTuningValuesVM } from 'components/commandpanel/ImageTuning/ImageTuningManager';
import { getTunedWellDropImage } from 'components/commandpanel/ImageTuning/utils';
import { CrystalClientDTO } from 'components/Experiment/constants';
import { getUpdatedImageTuningValues } from 'components/plateviewer/well/WellImage/utils';
import { DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE } from 'components/well/constants';
import {
  AnnotationDrawing,
  CrystalDrawing,
  DrawingFetchRequestParams,
  DrawingKey,
  DrawingType,
  DrawingUpdateRequestParams,
  DropImageRequestParams,
  DropLocationDrawing,
  LightPathDrawing,
  NewDrawingRequestParams,
  POIDrawing,
  ROIDrawing,
  RulerDrawing,
  ScribbleDrawing,
  SelectedDrawingDetails,
} from 'components/well/drawingManager/constants';
import EAlertType from 'enums/EAlertType';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { RootState } from 'redux/configureStore';
import {
  appendAndUpdateCrystalIdentifiers,
  appendAndUpdatePlateDrawingIcons,
  removeCanvasScribbleDrawing,
  removeFromPlateDrawing,
  toggleImportFl,
  updateCanvasScribbleDrawing,
} from 'redux/reducers/experimentDataReducer';
import {
  removeDropLocation,
  updateDrawing,
  setWellDropImageFullFilled,
  setWellDropImageStatus,
} from 'redux/reducers/wellReducer';
import {
  getTransformedNewDrawingsRequestParameters,
  getTransformedUpdateDrawingsRequestParameters,
  getWellDropImageUniqueKey,
} from 'redux/utils/well';
import { getPlainDto } from 'transformers/utils';
import {
  getPlainTransformedLightPathDrawingData,
  getPlainTransformedAnnotation,
  getPlainTransformedCrystal,
  getPlainTransformedPoi,
  getPlainTransformedRoi,
  getPlainTransformedRuler,
  getPlainTransformedScribbleDrawingData,
  getPlainTransformedDropLocation,
} from 'transformers/well';
import { getUserId, showAlertSnackBar } from 'utils/Global';
import drawingSaveManager from 'utils/DrawingSaveManager';

const wellService = new WellService();
const crystalService = new CrystalService();

export const fetchDrawingsThunk = createAsyncThunk<any, DrawingFetchRequestParams>(
  'fetchDrawingsThunk',
  async (param, thunkApi) => {
    try {
      const {
        plateId,
        wellNumber,
        dropNumber,
        batchId,
        captureProfileId,
        regionName,
        tpObject,
      } = param;
      const {
        roiList,
        rulerList,
        pointsList,
        annotationsList,
        crystalsList,
        allScribblePointofWell,
        lightPathList,
        imagingEnabled,
        regionDropLocation,
      } = await wellService.getWellDrawing(
        plateId,
        wellNumber,
        batchId,
        dropNumber,
        captureProfileId,
        regionName,
      );

      const rulers: RulerDrawing[] = rulerList.map((ruler) => {
        const rulerDrawing: RulerDrawing = getPlainTransformedRuler(ruler, tpObject);
        return rulerDrawing;
      });

      const rois: ROIDrawing[] = roiList.map((roi) => {
        const roiDrawing: ROIDrawing = getPlainTransformedRoi(roi, tpObject);
        return roiDrawing;
      });

      const pois: POIDrawing[] = pointsList.map((poi) => {
        const poiDrawing: POIDrawing = getPlainTransformedPoi(poi, tpObject);
        return poiDrawing;
      });

      const annotations: AnnotationDrawing[] = annotationsList.map((annotation) => {
        const annotationDrawing: AnnotationDrawing = getPlainTransformedAnnotation(
          annotation,
          tpObject,
        );
        return annotationDrawing;
      });

      const crystals: CrystalDrawing[] = crystalsList.map((crystal) => {
        const crystalDrawing: CrystalDrawing = getPlainTransformedCrystal(
          crystal,
          tpObject,
        );
        return crystalDrawing;
      });
      const scribbles: ScribbleDrawing[] = allScribblePointofWell.map((scribble) => {
        const scribbleDrawing: ScribbleDrawing = getPlainTransformedScribbleDrawingData(
          scribble,
          tpObject,
        );
        return scribbleDrawing;
      });
      const lightPaths: LightPathDrawing[] = lightPathList.map((lightPath) => {
        const lightPathDrawing: LightPathDrawing =
          getPlainTransformedLightPathDrawingData(lightPath, tpObject);
        return lightPathDrawing;
      });

      const dropLocations: DropLocationDrawing[] = regionDropLocation
        ? [getPlainTransformedDropLocation(regionDropLocation, tpObject)]
        : [];

      return {
        annotations,
        pois,
        crystals,
        rois,
        rulers,
        scribbles,
        lightPaths,
        imagingEnabled,
        dropLocations,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const updateDrawingThunk = createAsyncThunk<any, DrawingUpdateRequestParams>(
  'updateDrawingThunk',
  async (param, thunkApi) => {
    try {
      const {
        drawingId,
        drawingType,
        drawingUpdateDetails,
        tpObject,
        plateId,
        wellNumber,
        dropNumber,
      } = param;
      const updateParameters = getTransformedUpdateDrawingsRequestParameters(
        drawingType,
        drawingUpdateDetails,
        tpObject,
      );
      const updateDrawingDetails = new UpdateDrawingDTO({
        id: drawingId,
        drawingType: drawingType,
        ...updateParameters,
      });

      if (drawingType === DrawingType.DropLocation) {
        const { xOffSet, yOffSet, xWidth, yHeight } = updateParameters;
        const saveDropLocationDto = {
          pid: plateId,
          wellNumber: wellNumber,
          dropNumber: dropNumber,
          regionName: 'Drop',
          xcenterOffset: xOffSet,
          ycenterOffset: yOffSet,
          xwidth: xWidth,
          yheight: yHeight,
        };
        return wellService.saveRegionDropLocation(
          saveDropLocationDto as RegionDropLocationDTO,
        );
      } else {
        await wellService.UpdateDrawing(updateDrawingDetails);
      }
      return updateParameters;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const fetchDropImageThunk = createAsyncThunk<WellImageDTO, DropImageRequestParams>(
  'fetchDropImageThunk',
  async (params, thunkApi) => {
    try {
      const {
        pid,
        wellNumber,
        batchId,
        cpID,
        dropNumber,
        regionID,
        bestFocusImageRequired,
      } = params;
      const wellImageDto = await wellService.getWellImage(
        pid,
        wellNumber,
        batchId,
        dropNumber,
        cpID,
        regionID,
        bestFocusImageRequired,
      );
      return getPlainDto(wellImageDto);
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const saveDrawingThunk = createAsyncThunk<any, NewDrawingRequestParams>(
  'saveDrawingThunk',
  async (param, thunkApi) => {
    try {
      const {
        plateId,
        wellNumber,
        dropNumber,
        imageBatchId,
        cpId,
        regionName,
        drawingUpdateDetails,
        drawingType,
        tpObject,
      } = param;

      if (thunkApi.signal.aborted) {
        throw new Error('Save operation was aborted');
      }

      thunkApi.dispatch(
        updateDrawing({
          plateId,
          wellNumber,
          dropNumber,
          regionName,
          drawingType,
          id: drawingUpdateDetails.id,
          drawingUpdateDetails,
        }),
      );

      if (thunkApi.signal.aborted) {
        throw new Error('Save operation was aborted');
      }

      const updateParameters = getTransformedNewDrawingsRequestParameters(
        drawingType,
        drawingUpdateDetails,
        tpObject,
      );

      if (drawingType === DrawingType.CRYSTAL) {
        const { points, name } = updateParameters;
        const saveCrystalDetails = {
          x: points[0],
          y: points[1],
          width: points[2],
          height: points[3],
          name: name,
          imageBatchId: imageBatchId,
          captureProfileVersionId: cpId,
          dateCollected: null,
          dateFrozen: new Date(),
          plateId: plateId,
          wellNumber,
          dropNumber,
          regionName,
          ownerUserId: await getUserId(),
        } as CrystalClientDTO;

        if (thunkApi.signal.aborted) {
          throw new Error('Save operation was aborted');
        }

        // eslint-disable-next-line
        const { x, y, width, height, ...restCrystalData } =
          (await crystalService.CreateCrystal(
            saveCrystalDetails,
            thunkApi.signal,
          )) as CrystalClientDTO;
        thunkApi.dispatch(
          appendAndUpdateCrystalIdentifiers({
            plateId,
            wellNumber,
            dropNumber,
            regionName,
            cpId,
            imageBatchId,
          }),
        );
        return restCrystalData;
      } else if (drawingType === DrawingType.DropLocation) {
        const { points, regionName } = updateParameters;

        if (thunkApi.signal.aborted) {
          throw new Error('Save operation was aborted');
        }

        const saveDropLocationDto = new RegionDropLocationDTO({
          pid: plateId,
          wellNumber: wellNumber,
          dropNumber: dropNumber,
          regionName: regionName,
          xcenterOffset: points[0],
          ycenterOffset: points[1],
          xwidth: points[2],
          yheight: points[3],
        });
        return wellService.saveRegionDropLocation(saveDropLocationDto, thunkApi.signal);
      } else if (drawingType === DrawingType.Scribble) {
        if (thunkApi.signal.aborted) {
          throw new Error('Save operation was aborted');
        }

        const saveDrawingDetails = new RegionDrawingDTO({
          pid: plateId,
          wellNumber,
          dropNum: dropNumber,
          batchId: imageBatchId,
          cpId,
          type: drawingType,
          ...updateParameters,
          regionName,
        });
        const result = await wellService.addNewScribble(
          saveDrawingDetails,
          thunkApi.signal,
        );
        if (thunkApi.signal.aborted) {
          throw new Error('Save operation was aborted');
        }

        thunkApi.dispatch(
          updateCanvasScribbleDrawing({
            plateId: plateId,
            scribbles: result.allScribbles,
          }),
        );
        return result.drawingId;
      } else {
        if (thunkApi.signal.aborted) {
          throw new Error('Save operation was aborted');
        }

        const saveDrawingDetails = new RegionDrawingDTO({
          pid: plateId,
          wellNumber,
          dropNum: dropNumber,
          batchId: imageBatchId,
          cpId,
          regionName,
          type: drawingType,
          ...updateParameters,
        });
        const drawingId = await wellService.saveDrawing(
          saveDrawingDetails,
          thunkApi.signal,
        );

        if (thunkApi.signal.aborted) {
          throw new Error('Save operation was aborted');
        }

        if (drawingType == DrawingType.Annotation || drawingType == DrawingType.ROI) {
          thunkApi.dispatch(
            appendAndUpdatePlateDrawingIcons({
              plateId,
              wellNumber,
              dropNumber,
              drawingType,
            }),
          );
        }
        return drawingId;
      }
    } catch (error) {
      if (
        thunkApi.signal.aborted ||
        (error as Error)?.message === 'Save operation was aborted'
      ) {
        throw new Error('Save operation was aborted');
      }
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const fetchDrawingPreferences = createAsyncThunk<any, number>(
  'fetchDrawingPreferences',
  async (_, thunkApi) => {
    try {
      const globalPreferencesService = new GlobalPreferencesService();
      const [fontPreference, poiColor] = await Promise.all<any>([
        globalPreferencesService.getUserProperty(
          'Imaging.PlateViewer.AnnotationDefaultFont',
        ),
        globalPreferencesService.getProperty('Imaging.PlateViewer.PointOfInterestColor'),
      ]);
      let drawingPreferences = {};
      if (fontPreference) {
        const fontArray = fontPreference.value.split(',');
        const fontFamilyString = fontArray.length
          ? fontArray[0] === ''
            ? DEFAULT_FONT_FAMILY
            : fontArray[0]
          : DEFAULT_FONT_FAMILY;
        const fontSize =
          fontArray.length > 1
            ? parseInt(fontArray[1].replace(/\D/g, ''))
            : DEFAULT_FONT_SIZE;

        drawingPreferences = {
          ...drawingPreferences,
          fontPreferences: {
            fontFamily: fontFamilyString,
            fontSize: fontSize,
          },
        };
      }
      if (poiColor) {
        drawingPreferences = {
          ...drawingPreferences,
          poiColor: (poiColor as ColorPropertyDTO).value,
        };
      }

      return drawingPreferences;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const deleteDrawing = createAsyncThunk<any, SelectedDrawingDetails>(
  'deleteDrawing',
  async (param, thunkApi) => {
    try {
      if (!param) {
        return;
      }
      const { drawingId, drawingType, dropNumber, plateId, wellNumber, regionName } =
        param;

      const wasAborted = drawingSaveManager.abort(drawingId);
      if (!wasAborted) {
        const drawingToDelete = new DeleteScribbleDTO();
        drawingToDelete.id = param?.drawingId;
        drawingToDelete.type = param?.drawingType;
        const response = await wellService.DeleteDrawing(drawingToDelete);
        if (response.responseType === ResponseType.Error) {
          showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.message);
          return;
        }
      }
      if (drawingType == DrawingType.Annotation || drawingType == DrawingType.ROI) {
        const store = thunkApi.getState() as RootState;
        const allDrawings =
          store.well.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]
            ?.drawingsDetails?.[regionName]?.drawings;
        const drawings = allDrawings?.[DrawingKey[drawingType]];
        if (drawings?.length === 1) {
          thunkApi.dispatch(
            removeFromPlateDrawing({
              plateId,
              wellNumber,
              dropNumber,
              drawingType,
            }),
          );
        }
      } else if (drawingType === DrawingType.Scribble) {
        thunkApi.dispatch(
          removeCanvasScribbleDrawing({ plateId, wellNumber, dropNumber, drawingId }),
        );
      }
      return {
        plateId,
        wellNumber,
        dropNumber,
        regionName,
        drawingType,
        id: drawingId,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const toggleRoiImaging = createAsyncThunk<any, DrawingUpdateRequestParams>(
  'toggleRoiImaging',
  async (param, thunkApi) => {
    try {
      const { drawingId, drawingUpdateDetails } = param;
      const isSuccessful: boolean = await wellService.disableImaging(
        [drawingId],
        (drawingUpdateDetails as ROIDrawing).imagingEnabled,
      );
      if (isSuccessful) {
        return param;
      }
      return;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const fetchImagingDetails = createAsyncThunk<
  any,
  {
    expId: number;
    plateId: number;
    wellNumber: number;
    dropNumber: number;
    regionId: number;
    cpId: number;
    imageBatchId: number;
  }
>('fetchImagingDetails', async (param, thunkApi) => {
  try {
    const { expId, regionId, cpId, imageBatchId } = param;
    const info = await wellService.getWellImagingDetails(
      expId,
      regionId,
      cpId,
      imageBatchId,
    );
    return info;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const fetchWellImagesBulkAction = createAsyncThunk<
  WellImageDTO[],
  IWellImageParams[]
>('fetchWellImagesBulk', async (imageParams, thunkApi) => {
  try {
    const dataService = new WellService();
    return await dataService.getWellImagesBulk(
      imageParams.map((img) => WellImageParams.fromJS(img)),
    );
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const updateWellDropImportFl = createAsyncThunk<number, WellDropImportFlDTO>(
  'updateWellDropImportFl',
  async (params, thunkApi) => {
    try {
      thunkApi.dispatch(
        toggleImportFl({
          plateId: params.plateId,
          wellNumber: params.wellNumber,
          dropNumber: params.dropNumber,
          importFl: params.importFl,
        }),
      );
      const wellService = new WellService();
      return await wellService.updateWellDropImportFl(params);
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const setAutoDropLocation = createAsyncThunk<
  void,
  {
    plateId: number;
    wellNumber: number;
    dropNumber: number;
    regionName: string;
  },
  {
    rejectValue: string;
  }
>(
  'handleAutoDropLocation',
  async (
    { plateId, wellNumber, dropNumber, regionName },
    { dispatch, rejectWithValue },
  ) => {
    try {
      dispatch(
        removeDropLocation({
          plateId,
          wellNumber,
          dropNumber,
          regionName,
        }),
      );

      const saveRegionDropLocationDto = {
        pid: plateId,
        wellNumber: wellNumber,
        dropNumber: dropNumber,
        regionName: 'Drop',
        xcenterOffset: null,
        ycenterOffset: null,
        xwidth: null,
        yheight: null,
      } as RegionDropLocationDTO;

      await new WellService().saveRegionDropLocation(saveRegionDropLocationDto);
    } catch {
      return rejectWithValue('Failed to save region drop location');
    }
  },
);

export interface FetchImageWellDropMetaDataParams {
  experimentId: number;
  batchIds?: number[];
  inspectionId: number;
}

export const fetchWellDropImageMetaDataThunk = createAsyncThunk<
  Record<string, IWellImageDTO>,
  FetchImageWellDropMetaDataParams
>(
  'fetchWellDropImageMetaDataThunk',
  async (params, thunkApi) => {
    try {
      const wService = new WellService();

      return await wService.getWellIDropImageMetaData(
        params.experimentId,
        params.batchIds,
      );
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
  {
    condition: (params, { getState }) => {
      const state = getState() as RootState;
      const metaExistsAlready =
        state.well.wellDropImageMetaData.fetchedMetaDataInspectionIds[
          params.inspectionId
        ];
      return (
        metaExistsAlready !== EFetchingStatus.InProgress &&
        metaExistsAlready !== EFetchingStatus.Loaded
      );
    },
  },
);

export const fetchWellDropImageBinaryThunk =
  (
    requestParams: IWellImageDetailsDTO,
    imageUniqueKey: string,
    metaData: IWellImageDTO,
    experimentTuningValues: ExperimentTuningValuesDTO[],
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const wService = new WellService();

    try {
      const imageStore = getState().well.imageStore;
      if (
        imageStore[imageUniqueKey]?.fetchingStatus === EFetchingStatus.InProgress ||
        imageStore[imageUniqueKey]?.fetchingStatus === EFetchingStatus.Loaded ||
        !metaData
      ) {
        // If image is already in store, no need to fetch again
        return;
      }

      dispatch(
        setWellDropImageStatus({
          key: imageUniqueKey,
          status: EFetchingStatus.InProgress,
        }),
      );

      const response = await wService.getWellDropImageBinary(requestParams);
      if (response && response.headers['x-wellimagekey'] == imageUniqueKey) {
        const originalByteSize = response.data?.size ?? 0;
        const img = new window.Image();
        const imgUrl = window.URL.createObjectURL(response.data);

        img.src = imgUrl;
        img.onload = async () => {
          const onTuningCompletion = (tunedImage, tuningValues, tunedByteSize?) => {
            const updatedTuningValues = !metaData.captureResultTuningValuesDto
              ? null
              : getUpdatedImageTuningValues(
                  tuningValues,
                  metaData.captureResultTuningValuesDto,
                );

            const _payload = {
              key: imageUniqueKey,
              imageOrg: img,
              imageTuned: tunedImage,
              // only include updated tuning value if its avaiable for drop., no need to include it for entire experiment
              // we can get the tuning values from experimentTuningValues on run time if the showoverride is disabled.
              updatedTuningValues: updatedTuningValues,
              plateTuningValues: tuningValues,
              originalByteSize,
              tunedByteSize,
            };

            dispatch(setWellDropImageFullFilled(_payload));
          };

          /**
           * IF tuning values are available for entire experiment or available for the specific drop using metadata.
           */
          if (
            metaData.captureResultTuningValuesDto ||
            (experimentTuningValues && experimentTuningValues.length > 0)
          ) {
            getTunedWellDropImage(
              experimentTuningValues,
              img,
              metaData.cpId,
              metaData.captureResultTuningValuesDto,
              onTuningCompletion,
            );
          } else {
            dispatch(
              setWellDropImageFullFilled({
                key: imageUniqueKey,
                imageOrg: img,
                imageTuned: null,
                originalByteSize,
              }),
            );
          }
        };

        img.onerror = () => {
          dispatch(
            setWellDropImageStatus({
              key: imageUniqueKey,
              status: EFetchingStatus.Error,
            }),
          );
        };
      }
    } catch {
      dispatch(
        setWellDropImageStatus({ key: imageUniqueKey, status: EFetchingStatus.Error }),
      );
    }
  };

export const saveWellDropTuningValuesThunk = createAsyncThunk<
  {
    uIKey: string;
    tuningValues: CaptureResultTuningValuesDTO;
  },
  {
    lowerLimit: number;
    upperLimit: number;
    captureProfileId: number;
    dropRegionId: number;
    imageBatchId: number;
    isAutoLevelingOn: boolean;
    uIKeyParams: IWellImageParams;
  },
  {
    rejectValue: string;
  }
>(
  'saveWellDropTuningValuesThunk',
  async (
    {
      lowerLimit,
      upperLimit,
      captureProfileId,
      dropRegionId,
      imageBatchId,
      isAutoLevelingOn,
      uIKeyParams,
    },
    { rejectWithValue },
  ) => {
    try {
      const tuningValuesVM = getDropTuningValuesVM(
        lowerLimit,
        upperLimit,
        captureProfileId,
        dropRegionId,
        isAutoLevelingOn,
      );

      if (!tuningValuesVM) {
        return;
      }

      const tuningValuesDto = new CaptureResultTuningValuesDTO();
      tuningValuesDto.autoLevelingHighThreshold =
        tuningValuesVM.autoLevelingHighThreshold;
      tuningValuesDto.autoLevelingLowThreshold = tuningValuesVM.autoLevelingLowThreshold;
      tuningValuesDto.isAutoLevelingOn = tuningValuesVM.isAutoLevelingOn;
      tuningValuesDto.lowerLevelLimit = tuningValuesVM.lowerLevelLimit;
      tuningValuesDto.upperLevelLimit = tuningValuesVM.upperLevelLimit;
      tuningValuesDto.cpId = captureProfileId;
      tuningValuesDto.imageBatchId = imageBatchId;
      tuningValuesDto.regionID = dropRegionId;

      const tValues = await new WellService().savecaptureresulttuningvalues(
        tuningValuesDto,
      );

      return {
        tuningValues: tValues,
        uIKey: getWellDropImageUniqueKey(uIKeyParams),
      };
    } catch {
      return rejectWithValue('Failed to save region drop location');
    }
  },
);
